import { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { AdjustBox, AdjustSource } from './useImageCardsSplit';

const NO_BOXES: AdjustBox[] = [];
const NO_HEADINGS: string[] = [];

function errText(err: unknown): string {
  return (
    (err as { error?: string; message?: string })?.error ??
    (err as Error)?.message ??
    'Could not read that screenshot.'
  );
}

function persistCropSource(
  opts: {
    modelId: string;
    modelType: string;
    contentBlockId?: string;
    backendService?: BackendService;
  },
  source: AdjustSource,
  boxes: AdjustBox[],
) {
  if (!opts.contentBlockId || !opts.backendService?.saveImageCardCropSource) return;
  opts.backendService
    .saveImageCardCropSource({
      modelType: opts.modelType,
      modelId: opts.modelId,
      contentBlockId: opts.contentBlockId,
      source,
      boxes,
    })
    .catch((err) => console.warn('Could not save the crop source for Adjust crops:', err));
}

function mergeRecroppedItems<P>(props: P, items: Array<{ imageUrl: unknown }>): P {
  const src = props as { items?: Array<Record<string, unknown>> };
  return {
    ...(props as object),
    items: (src.items ?? []).map((it, i) =>
      items[i] ? { ...it, imageUrl: items[i].imageUrl } : it,
    ),
  } as P;
}

export function useCropAdjust<P>(opts: {
  modelId: string;
  modelType: string;
  contentBlockId?: string;
  backendService?: BackendService;
  apply: (props: P) => void;
  setBusy: (b: boolean) => void;
  setError: (e: string | null) => void;
}) {
  const [last, setLast] = useState<{
    source: AdjustSource;
    boxes: AdjustBox[];
    props: P;
  } | null>(null);
  const [open, setOpen] = useState(false);

  const applyAdjust = async (boxes: AdjustBox[]) => {
    if (!last || !opts.backendService?.recropImageCards) return;
    opts.setBusy(true);
    opts.setError(null);
    try {
      const { items } = await opts.backendService.recropImageCards(
        opts.modelType,
        opts.modelId,
        last.source.path,
        boxes,
      );
      const merged = mergeRecroppedItems(last.props, items);
      setLast({ ...last, boxes, props: merged });
      persistCropSource(opts, last.source, boxes);
      setOpen(false);
      opts.apply(merged);
    } catch (err) {
      opts.setError(errText(err));
    } finally {
      opts.setBusy(false);
    }
  };

  const remember = (
    props: P,
    source: AdjustSource | null,
    boxes: AdjustBox[],
    options: { persist?: boolean } = {},
  ) => {
    setLast(source ? { source, boxes, props } : null);
    if (source && options.persist !== false) persistCropSource(opts, source, boxes);
  };

  return [
    {
      canAdjust: last !== null,
      adjustOpen: open,
      adjustSource: last?.source ?? null,
      adjustBoxes: last?.boxes ?? NO_BOXES,
      adjustHeadings: last
        ? ((last.props as { items?: Array<{ heading?: { content?: string } }> }).items ?? []).map(
            (it) => it.heading?.content ?? '',
          )
        : NO_HEADINGS,
      openAdjust: () => {
        if (last) setOpen(true);
      },
      cancelAdjust: () => setOpen(false),
      applyAdjust,
    },
    remember,
  ] as const;
}
